import { LOCALES, isLocaleCode, resolveDashboardLocale, type LanguageContext, type LocaleCode } from "./locales";

export type LanguageState = {
  locales: typeof LOCALES;
  mode: "guest" | "authenticated" | "impersonating";
  generation: number;
  accountLocale: LocaleCode | null;
  sessionLocale: LocaleCode | null;
  guestLocale: LocaleCode | null;
  effectiveLocale: LocaleCode;
  displayedLocale: LocaleCode;
  source: LanguageContext["source"] | "guest";
  contextStatus: "loading" | "ready" | "error" | "stale";
  saveStatus: "idle" | "saving" | "saved" | "error" | "conflict" | "uncertain";
  catalogStatus: "ready" | "loading" | "error";
  storageAvailable: boolean;
  errorCode: "unauthorized" | "forbidden" | "invalid" | "conflict" | "network" | "bootstrap" | null;
};
export type LanguageChoice = { scope: "session" | "account" | "guest"; locale: LocaleCode | null };
type Dependencies = {
  languages: readonly string[];
  readGuest: () => string | null;
  writeGuest: (locale: LocaleCode | null) => void;
  load: (locale: LocaleCode) => Promise<() => void>;
  save: (choice: LanguageChoice, language: LanguageContext) => Promise<{ status: number; code?: string; language?: LanguageContext }>;
  hint: () => void;
};

export function createLanguageController(deps: Dependencies) {
  let storageAvailable = true;
  let guestLocale: LocaleCode | null = null;
  try { const saved = deps.readGuest(); if (isLocaleCode(saved)) guestLocale = saved; } catch { storageAvailable = false; }
  const initial = resolveDashboardLocale({ browserLanguages: deps.languages });
  let state: LanguageState = {
    locales: LOCALES, mode: "guest", generation: 0, accountLocale: null, sessionLocale: null,
    guestLocale, effectiveLocale: guestLocale ?? initial.locale, displayedLocale: "en",
    source: guestLocale ? "guest" : initial.source, contextStatus: "loading", saveStatus: "idle",
    catalogStatus: "ready", storageAvailable, errorCode: null,
  };
  let identity: string | null = null;
  let context: LanguageContext | null = null;
  let pendingContextReview: string | null = null;
  let dashboardDefaultLocale: LocaleCode | null = null;
  let catalogGeneration = 0;
  let refresh: () => Promise<void> = async () => {};
  let reloadPublicConfig: () => Promise<void> = async () => {};
  const listeners = new Set<() => void>();
  const publish = (patch: Partial<LanguageState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener());
  };
  const writeGuest = (locale: LocaleCode | null) => {
    try { deps.writeGuest(locale); } catch { publish({ storageAvailable: false }); }
  };
  const retryCatalog = async () => {
    const ticket = ++catalogGeneration;
    const generation = state.generation;
    const locale = state.effectiveLocale;
    publish({ catalogStatus: "loading" });
    try {
      const activate = await deps.load(locale);
      if (ticket !== catalogGeneration || generation !== state.generation) return;
      activate();
      publish({ displayedLocale: locale, catalogStatus: "ready" });
    } catch {
      if (ticket === catalogGeneration && generation === state.generation) publish({ catalogStatus: "error" });
    }
  };
  const resolveGuest = () => {
    const resolved = resolveDashboardLocale({ browserLanguages: deps.languages, dashboardDefaultLocale });
    publish({ effectiveLocale: state.guestLocale ?? resolved.locale, source: state.guestLocale ? "guest" : resolved.source });
    void retryCatalog();
  };
  const acceptContext = (language: LanguageContext) => {
    if (context && (language.revision.account < context.revision.account || language.revision.session < context.revision.session)) return;
    context = language;
    if (state.saveStatus === "uncertain") publish({ saveStatus: "idle", errorCode: null });
    const changed = state.effectiveLocale !== language.locale;
    publish({ accountLocale: language.accountLocale, sessionLocale: language.sessionLocale, effectiveLocale: language.locale,
      source: language.source, contextStatus: "ready" });
    if (changed || state.catalogStatus === "error") void retryCatalog();
  };
  const setIdentity = (key: string | null, impersonating: boolean, language: LanguageContext | null) => {
    const changed = identity !== key;
    if (changed) {
      const preserveReview = Boolean(key && language && pendingContextReview === language.sessionContext);
      pendingContextReview = null;
      identity = key; context = null; catalogGeneration++;
      if (key) writeGuest(null);
      publish({ generation: state.generation + 1, saveStatus: preserveReview ? "conflict" : "idle", errorCode: preserveReview ? "conflict" : null,
        guestLocale: key ? null : state.guestLocale, accountLocale: null, sessionLocale: null });
      deps.hint();
    }
    publish({ mode: key ? (impersonating ? "impersonating" : "authenticated") : "guest", contextStatus: key && !language ? "error" : "ready" });
    if (key && language) { acceptContext(language); if (changed) void retryCatalog(); }
    else if (!key) resolveGuest();
  };
  const invalidateIdentity = () => {
    const hadIdentity = identity !== null;
    catalogGeneration++; context = null; identity = null; pendingContextReview = null;
    publish({ generation: state.generation + 1, mode: "guest", accountLocale: null, sessionLocale: null,
      guestLocale: null, saveStatus: "idle", errorCode: null, contextStatus: "ready" });
    writeGuest(null); resolveGuest();
    if (hadIdentity) deps.hint();
  };
  const apply = async (choice: LanguageChoice) => {
    if (state.saveStatus === "saving" || state.saveStatus === "uncertain") return;
    if (choice.locale !== null && !isLocaleCode(choice.locale)) { publish({ saveStatus: "error", errorCode: "invalid" }); return; }
    if (state.mode === "guest") {
      if (choice.scope !== "guest") return;
      writeGuest(choice.locale);
      publish({ guestLocale: choice.locale, saveStatus: "saved", errorCode: null }); resolveGuest(); return;
    }
    if (!context || state.contextStatus !== "ready") return;
    if (choice.scope === "guest" || (choice.scope === "account" && state.mode === "impersonating")) {
      publish({ saveStatus: "error", errorCode: "forbidden" }); return;
    }
    const generation = state.generation;
    pendingContextReview = null;
    publish({ saveStatus: "saving", errorCode: null });
    try {
      const result = await deps.save(choice, context);
      if (generation !== state.generation) return;
      if (result.status === 409 && result.code === "LANGUAGE_CONTEXT_CHANGED") {
        catalogGeneration++;
        pendingContextReview = result.language?.sessionContext ?? null;
        publish({ generation: state.generation + 1, saveStatus: "conflict", errorCode: "conflict", contextStatus: "stale" });
        await refresh(); return;
      }
      if (result.status === 409) {
        if (result.language) acceptContext(result.language);
        publish({ saveStatus: "conflict", errorCode: "conflict" });
        await refresh(); return;
      }
      if (result.status === 401) {
        invalidateIdentity(); publish({ saveStatus: "error", errorCode: "unauthorized" }); await refresh(); return;
      }
      if (result.status < 200 || result.status >= 300 || !result.language) {
        // A success envelope without valid context may still have committed.
        const uncertain = result.status >= 500 || (result.status >= 200 && result.status < 300);
        publish({ saveStatus: uncertain ? "uncertain" : "error", errorCode: result.status === 403 ? "forbidden" : uncertain ? "network" : "invalid" }); return;
      }
      acceptContext(result.language);
      publish({ saveStatus: "saved", errorCode: null });
      deps.hint();
    } catch {
      if (generation === state.generation) publish({ saveStatus: "uncertain", errorCode: "network" });
    }
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    setIdentity, invalidateIdentity, apply, retryCatalog,
    retryContext: async () => { await refresh(); if (!identity) await reloadPublicConfig(); },
    setRefresh: (callback: () => Promise<void>) => { refresh = callback; },
    setPublicConfigRefresh: (callback: () => Promise<void>) => { reloadPublicConfig = callback; },
    contextFailed: () => publish({ contextStatus: identity ? "stale" : "error", errorCode: "bootstrap" }),
    publicConfigFailed: () => { if (!identity) publish({ contextStatus: "error", errorCode: "bootstrap" }); },
    setDashboardDefault: (locale: LocaleCode | null) => { dashboardDefaultLocale = locale; if (!identity) { publish({ contextStatus: "ready", errorCode: null }); resolveGuest(); } },
  };
}
export type LanguageController = ReturnType<typeof createLanguageController>;
